export type LoginType = "ERP" | "PLATFORM";
export * from "./commercial";
export * from "./enterprise-references";
export * from "./operational-documents";

export interface LoginRequest {
  loginType: LoginType;
  tenantCode?: string;
  username: string;
  password: string;
}

export interface TenantFilters {
  packageId?: string | undefined;
  expiry?: string | undefined;
  expiresFrom?: string | undefined;
  expiresTo?: string | undefined;
  quotaWarning?: boolean | undefined;
  sort?: string | undefined;
}
export interface TenantDirectoryOverview {
  total: number;
  serviceActive: number;
  dueIn7Days: number;
  dueIn30Days: number;
  expired: number;
  quotaWarning: number;
}
export interface TenantAccessIssued {
  id: string;
  token: string;
  expiresAt: string;
  enterpriseUsername: string;
}
export interface TenantAccessGrant {
  id: string;
  issuerName: string;
  issuedAt: string;
  expiresAt: string;
  status: "ISSUED" | "USED" | "REVOKED" | "EXPIRED";
  reason: string;
  canRevoke: boolean;
}
export interface SessionUser {
  id: string;
  displayName: string;
  username: string;
  loginType: LoginType;
  tenantId?: string;
  tenantCode?: string;
  tenantName?: string;
  permissions: string[];
  platformAccess?: { actorName: string; expiresAt: string } | null;
}

/** These independent public demonstration accounts may inspect forms, but cannot write. */
export function isProtectedDemoViewer(
  user: SessionUser | null | undefined,
): boolean {
  return (
    user?.loginType === "ERP" &&
    user.username === "demo_viewer" &&
    (user.tenantCode === "XINGYUN_MFG" || user.tenantCode === "XINGYUN_TRADE")
  );
}

export interface PlatformAccount {
  id: string;
  username: string;
  displayName: string;
  accountLevel: "SUPER_ADMIN" | "PLATFORM_ADMIN";
  status: "ACTIVE" | "DISABLED";
  createdAt: string;
  lastLoginAt: string | null;
  version: number;
}

export interface PlatformAccountCreate {
  username: string;
  displayName: string;
  password: string;
  reason: string;
}

export interface PlatformAccountUpdate {
  username: string;
  displayName: string;
  status: "ACTIVE" | "DISABLED";
  version: number;
  reason: string;
}

export interface PlatformAuditEvent {
  id: string;
  tenantId: string | null;
  tenantName: string;
  actorName: string;
  actorType: string;
  action: string;
  targetType: string;
  targetId: string | null;
  reason: string | null;
  traceId: string;
  at: string;
}

export interface PlatformOperationsOverview {
  tenants: number;
  serviceActive: number;
  expired: number;
  failedTasks24h: number;
  staleTasks: number;
  failedImports7d: number;
  storageNearLimit: number;
}

export interface PlatformTenantSignal {
  id: string;
  code: string;
  name: string;
  serviceStatus: TenantStatus;
  expiresAt: string;
  packageName: string;
  storageUsedGb: number;
  storageLimitGb: number;
  failedTasks24h: number;
  staleTasks: number;
  failedImports7d: number;
  storageNearLimit: boolean;
}

export interface PlatformTaskIssue {
  id: string;
  tenantCode: string;
  tenantName: string;
  number: string;
  taskType: string;
  status: "FAILED" | "RUNNING";
  updatedAt: string;
}

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message: string;
  traceId: string;
  timestamp: string;
}

export interface PageResult<T> {
  items: T[];
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

export type TenantStatus = "ACTIVE" | "TRIAL" | "SUSPENDED" | "EXPIRED";

export interface TenantListItem {
  id: string;
  code: string;
  name: string;
  status: TenantStatus;
  packageName: string;
  packageId?: string;
  productUsage?: number;
  productLimit?: number;
  storageUsedGb?: number;
  storageLimitGb?: number;
  quotaWarning?: boolean;
  expiresAt: string;
  userUsage: number;
  userLimit: number;
  warehouseUsage: number;
  warehouseLimit: number;
  createdAt: string;
}

export interface PackageOption {
  id: string;
  code: string;
  name: string;
  description: string;
  userLimit: number;
  warehouseLimit: number;
  productLimit: number;
  storageLimitGb: number;
  permissionCodes: string[];
}

export interface TenantCreateRequest {
  code: string;
  name: string;
  packageId: string;
  expiresAt: string;
  adminName: string;
  adminUsername: string;
  adminPassword: string;
}

export interface WorkspaceSummary {
  tenant: {
    name: string;
    code: string;
    status: TenantStatus;
    packageName: string;
    expiresAt: string;
  };
  readiness: number;
  organization: {
    userCount: number;
    departmentCount: number;
    activeRoleCount: number;
  };
  quota: {
    users: { used: number; limit: number };
    warehouses: { used: number; limit: number };
    products: { used: number; limit: number };
    storageGb: { used: number; limit: number };
  };
  setupTasks: Array<{
    key: string;
    title: string;
    description: string;
    status: "DONE" | "READY" | "LOCKED";
    route?: string;
  }>;
  moduleStates: Array<{
    key: string;
    name: string;
    phase: string;
    status: "AVAILABLE" | "BUILDING" | "PLANNED";
  }>;
  packagePermissionCodes: string[];
}

export interface DepartmentItem {
  id: string;
  code: string;
  name: string;
  parentId?: string;
  managerName?: string;
  employeeCount: number;
  status: "ACTIVE" | "DISABLED";
  version: number;
}

export interface EmployeeItem {
  id: string;
  employeeNo: string;
  displayName: string;
  username: string;
  departmentName?: string;
  mobileMasked?: string;
  status: "ACTIVE" | "DISABLED" | "LOCKED";
  roleNames: string[];
  lastLoginAt?: string;
}

export interface RoleOption {
  id: string;
  code: string;
  name: string;
  description?: string;
}

export interface DepartmentCreateRequest {
  code: string;
  name: string;
  parentId?: string;
  managerName?: string;
}

export interface EmployeeCreateRequest {
  employeeNo: string;
  displayName: string;
  username: string;
  password: string;
  departmentId?: string;
  mobile?: string;
  roleIds: string[];
}

export type RecordStatus = "ACTIVE" | "DISABLED";
export type AuditStatus = "DRAFT" | "APPROVED" | "REJECTED";
export type FulfillmentStatus = "NONE" | "PARTIAL" | "FULL";

export interface UnitItem {
  id: string;
  code: string;
  name: string;
  decimalPlaces: number;
  status: RecordStatus;
  version: number;
}

export interface UnitCreateRequest {
  code?: string;
  name: string;
  decimalPlaces: number;
}

export interface CategoryItem {
  id: string;
  code: string;
  name: string;
  parentId?: string;
  status: RecordStatus;
  version: number;
}

export interface CategoryCreateRequest {
  code?: string;
  name: string;
  parentId?: string | undefined;
}

export interface ProductItem {
  id: string;
  code: string;
  name: string;
  specification?: string;
  categoryId?: string;
  categoryName?: string;
  unitId: string;
  unitName: string;
  barcode?: string;
  trackingMode: "NONE" | "BATCH" | "SERIAL";
  safetyStock: number;
  salePrice: number;
  purchasePrice?: number;
  taxRate: number;
  status: RecordStatus;
  version: number;
}

export interface CustomerItem {
  id: string;
  code: string;
  name: string;
  shortName?: string;
  contactName?: string;
  mobile?: string;
  creditLimit: number;
  accountPeriodDays: number;
  ownerName?: string;
  address?: string;
  status: RecordStatus;
  version: number;
}

export interface SupplierItem {
  id: string;
  code: string;
  name: string;
  shortName?: string;
  contactName?: string;
  mobile?: string;
  settlementMethod: "MONTHLY" | "DELIVERY" | "PREPAID";
  accountPeriodDays: number;
  deliveryDays: number;
  purchaseCategories?: string;
  address?: string;
  status: RecordStatus;
  version: number;
}

export interface WarehouseItem {
  id: string;
  code: string;
  name: string;
  managerName?: string;
  address?: string;
  allowNegativeStock: boolean;
  status: RecordStatus;
  version: number;
}

export interface ProductOption {
  id: string;
  code: string;
  name: string;
  specification?: string;
  unitName: string;
  trackingMode: "NONE" | "BATCH" | "SERIAL";
  salePrice: number;
  purchasePrice?: number;
  taxRate: number;
}

export interface PartnerOption {
  id: string;
  code: string;
  name: string;
  accountPeriodDays: number;
}

export interface WarehouseOption {
  id: string;
  code: string;
  name: string;
}

export interface MasterDataOptions {
  products: ProductOption[];
  customers: PartnerOption[];
  suppliers: PartnerOption[];
  warehouses: WarehouseOption[];
  units: UnitItem[];
  categories: CategoryItem[];
}

export interface ProductCreateRequest {
  code?: string;
  name: string;
  specification?: string | undefined;
  categoryId?: string | undefined;
  unitId: string;
  barcode?: string | undefined;
  trackingMode: "NONE" | "BATCH" | "SERIAL";
  safetyStock: number;
  salePrice: number;
  purchasePrice: number;
  taxRate: number;
}

export interface CustomerCreateRequest {
  code?: string;
  name: string;
  shortName?: string | undefined;
  contactName?: string | undefined;
  mobile?: string | undefined;
  creditLimit: number;
  accountPeriodDays: number;
  ownerName?: string | undefined;
  address?: string | undefined;
}

export interface SupplierCreateRequest {
  code?: string;
  name: string;
  shortName?: string | undefined;
  contactName?: string | undefined;
  mobile?: string | undefined;
  settlementMethod: "MONTHLY" | "DELIVERY" | "PREPAID";
  accountPeriodDays: number;
  deliveryDays: number;
  purchaseCategories?: string | undefined;
  address?: string | undefined;
}

export interface WarehouseCreateRequest {
  code?: string;
  name: string;
  managerName?: string | undefined;
  address?: string | undefined;
}

export interface TradeLineRequest {
  productId: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

export interface TradeOrderCreateRequest {
  customerId?: string | undefined;
  supplierId?: string | undefined;
  orderDate: string;
  deliveryDate?: string | undefined;
  expectedDate?: string | undefined;
  remark?: string | undefined;
  items: TradeLineRequest[];
}

export interface TradeOrderItem {
  id: string;
  lineNo: number;
  productId: string;
  productCode: string;
  productName: string;
  specification?: string;
  unitName: string;
  trackingMode: "NONE" | "BATCH" | "SERIAL";
  quantity: number;
  unitPrice: number | null;
  taxRate: number;
  amount: number | null;
  taxAmount: number | null;
  totalAmount: number | null;
  shippedQuantity?: number;
  receivedQuantity?: number;
  returnedQuantity: number;
  remainingQuantity: number;
}

export interface TradeOrder {
  id: string;
  orderNo: string;
  customerId?: string;
  customerCode?: string;
  customerName?: string;
  supplierId?: string;
  supplierCode?: string;
  supplierName?: string;
  orderDate: string;
  deliveryDate?: string;
  expectedDate?: string;
  amount: number | null;
  taxAmount: number | null;
  totalAmount: number | null;
  auditStatus: AuditStatus;
  businessStatus: "OPEN" | "COMPLETED" | "CLOSED" | "VOID";
  fulfillmentStatus: FulfillmentStatus;
  settlementStatus: FulfillmentStatus;
  remark?: string;
  createdAt: string;
  version: number;
  items: TradeOrderItem[];
}

export interface TradeSummary {
  completedOrders: number;
  draftOrders: number;
  approvedOpenOrders: number;
  pendingOutboundQuantity?: number;
  pendingInboundQuantity?: number;
  openReceivableAmount?: number;
  openPayableAmount?: number;
}

export interface TradeExecutionItem {
  id: string;
  orderItemId: string;
  productId: string;
  productCode: string;
  productName: string;
  batchNo: string;
  quantity: number;
  unitPrice: number | null;
  taxRate: number;
  totalAmount: number | null;
  unitCost?: number;
  costAmount?: number;
  returnedQuantity: number;
  returnableQuantity: number;
}

export interface TradeExecutionDocument {
  id: string;
  outboundNo?: string;
  inboundNo?: string;
  orderId: string | null;
  orderNo: string | null;
  customerName?: string;
  supplierName?: string;
  warehouseId: string;
  warehouseName: string;
  businessDate: string;
  totalAmount: number | null;
  costAmount?: number;
  auditStatus: "DRAFT" | "APPROVED";
  remark?: string;
  auditedAt: string;
  items: TradeExecutionItem[];
}

export interface TradeExecutionRequest {
  orderId: string;
  warehouseId: string;
  businessDate: string;
  remark?: string | undefined;
  items: Array<{
    orderItemId: string;
    batchNo?: string | undefined;
    quantity: number;
    locationId?: string | undefined;
  }>;
}

export interface DirectTradeExecutionRequest {
  customerId?: string;
  supplierId?: string;
  warehouseId: string;
  businessDate: string;
  remark?: string;
  items: Array<{
    productId: string;
    batchNo?: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
    locationId?: string;
  }>;
}

export interface TradeReturnDocument {
  id: string;
  returnNo: string;
  outboundId?: string;
  outboundNo?: string;
  inboundId?: string;
  inboundNo?: string;
  customerName?: string;
  supplierName?: string;
  warehouseName: string;
  businessDate: string;
  totalAmount: number | null;
  costAmount?: number;
  reason: string;
  auditStatus: "APPROVED";
  auditedAt: string;
  items: Array<{
    id: string;
    outboundItemId?: string;
    inboundItemId?: string;
    productId: string;
    productCode: string;
    productName: string;
    batchNo: string;
    quantity: number;
    unitPrice: number | null;
    unitCost?: number;
    totalAmount: number | null;
    costAmount?: number;
  }>;
}

export interface TradeReturnRequest {
  outboundId?: string;
  inboundId?: string;
  businessDate: string;
  reason: string;
  items: Array<{
    outboundItemId?: string;
    inboundItemId?: string;
    quantity: number;
    locationId?: string | undefined;
  }>;
}

export interface InventorySummary {
  skuCount: number;
  lowStockCount: number;
  totalQuantity: number;
  totalAmount?: number;
  costVisible: boolean;
}

export interface StockBalance {
  id: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  productId: string;
  productCode: string;
  productName: string;
  specification?: string;
  unitName: string;
  trackingMode: "NONE" | "BATCH" | "SERIAL";
  batchNo: string;
  quantity: number;
  lockedQuantity: number;
  availableQuantity: number;
  safetyStock: number;
  lowStock: boolean;
  unitCost?: number;
  amount?: number;
  version: number;
}

export interface StockLog {
  id: string;
  warehouseCode: string;
  warehouseName: string;
  productCode: string;
  productName: string;
  batchNo: string;
  direction: "IN" | "OUT" | "ADJUST";
  businessType: string;
  businessNo: string;
  beforeQuantity: number;
  changeQuantity: number;
  afterQuantity: number;
  unitCost?: number;
  amount?: number;
  businessDate: string;
  occurredAt: string;
}

export interface MiscStockMovement {
  id: string;
  documentNo: string;
  kind: "OTHER_INBOUND" | "OTHER_OUTBOUND";
  warehouseId: string;
  warehouseName: string;
  businessDate: string;
  reason: string;
  auditStatus: "APPROVED";
  totalAmount: number | null;
  auditedAt: string;
  items: Array<{
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    batchNo: string;
    quantity: number;
    unitCost: number | null;
    amount: number | null;
  }>;
}

export interface MiscStockMovementRequest {
  kind: "OTHER_INBOUND" | "OTHER_OUTBOUND";
  warehouseId: string;
  businessDate: string;
  reason: string;
  items: Array<{
    productId: string;
    batchNo?: string;
    quantity: number;
    unitCost?: number;
    locationId?: string;
  }>;
}

export interface StockMovementLine {
  productId: string;
  batchNo?: string | undefined;
  quantity: number;
}

export interface StockTransferRequest {
  sourceWarehouseId: string;
  targetWarehouseId: string;
  businessDate: string;
  transferMode: "ONE_STEP" | "TWO_STEP";
  reason: string;
  items: StockMovementLine[];
}

export interface StockTransferActionRequest {
  businessDate: string;
  reason: string;
  version: number;
}

export interface StockCheckRequest {
  warehouseId: string;
  businessDate: string;
  reason: string;
  items: Array<{
    productId: string;
    batchNo?: string | undefined;
    actualQuantity: number;
    unitCost?: number | undefined;
  }>;
}

export interface StockTransfer {
  id: string;
  transferNo: string;
  sourceWarehouseName: string;
  targetWarehouseName: string;
  businessDate: string;
  reason: string;
  transferMode: "ONE_STEP" | "TWO_STEP";
  transferStatus: "IN_TRANSIT" | "COMPLETED" | "CANCELLED";
  auditStatus: "APPROVED";
  auditedAt: string;
  receiptDate?: string;
  receivedAt?: string;
  version: number;
  items: Array<
    StockMovementLine & {
      id: string;
      productCode: string;
      productName: string;
      unitCost?: number;
      transferAmount?: number;
    }
  >;
}

export interface StockCheck {
  id: string;
  checkNo: string;
  warehouseName: string;
  businessDate: string;
  reason: string;
  auditStatus: "APPROVED";
  auditedAt: string;
  items: Array<{
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    batchNo: string;
    systemQuantity: number;
    actualQuantity: number;
    differenceQuantity: number;
    unitCost?: number;
  }>;
}

export type OpenItemDirection = "RECEIVABLE" | "PAYABLE";
export type CashDirection = "RECEIPT" | "PAYMENT";
export type FinanceDocumentStatus =
  "DRAFT" | "AUDITED" | "POSTED" | "CANCELLED" | "REVERSED";

export interface FinanceSummary {
  receivableOutstanding: number;
  payableOutstanding: number;
  overdueReceivable: number;
  overduePayable: number;
  cashBalance: number;
  draftCashOrders: number;
  unpostedVouchers: number;
}

export interface OpenItem {
  id: string;
  direction: OpenItemDirection;
  partnerId: string;
  partnerCode: string;
  partnerName: string;
  sourceType: string;
  sourceId: string;
  sourceNo: string;
  businessDate: string;
  dueDate?: string;
  originalAmount: number;
  outstandingAmount: number;
  status: "OPEN" | "PARTIAL" | "SETTLED" | "REVERSED";
  overdueDays: number;
}

export interface AgingBucket {
  key: string;
  label: string;
  receivableAmount: number;
  payableAmount: number;
}

export interface SettlementAccount {
  id: string;
  code: string;
  name: string;
  accountType: "CASH" | "BANK";
  bankName?: string;
  accountNo?: string;
  currentBalance: number;
  status: RecordStatus;
  version: number;
}

export interface CashAllocation {
  id: string;
  sourceId: string;
  sourceNo: string;
  amount: number;
}

export interface CashOrder {
  id: string;
  direction: CashDirection;
  number: string;
  partnerId: string;
  partnerCode: string;
  partnerName: string;
  settlementAccountId: string;
  settlementAccountName: string;
  businessDate: string;
  amount: number;
  allocatedAmount: number;
  unappliedAmount: number;
  status: "DRAFT" | "AUDITED" | "CANCELLED" | "REVERSED";
  remark?: string;
  version: number;
  createdAt: string;
  allocations: CashAllocation[];
}

export interface CashOrderCreateRequest {
  partnerId: string;
  settlementAccountId: string;
  businessDate: string;
  amount: number;
  allocations: Array<{ sourceId: string; amount: number }>;
  remark?: string | undefined;
}

export interface AccountSubject {
  id: string;
  code: string;
  name: string;
  category: "ASSET" | "LIABILITY" | "EQUITY" | "COST" | "PROFIT_LOSS";
  balanceDirection: "DEBIT" | "CREDIT";
  auxiliaryType: "NONE" | "CUSTOMER" | "SUPPLIER" | "DEPARTMENT";
  systemDefined: boolean;
  status: RecordStatus;
  version: number;
}

export interface AccountingPeriod {
  id: string;
  year: number;
  month: number;
  startDate: string;
  endDate: string;
  status: "OPEN" | "CLOSED";
  version: number;
}

export interface AccountSet {
  id: string;
  code: string;
  name: string;
  baseCurrency: string;
  status: RecordStatus;
  version: number;
}

export interface SubjectWriteRequest {
  code: string;
  name: string;
  category: AccountSubject["category"];
  balanceDirection: AccountSubject["balanceDirection"];
  auxiliaryType: AccountSubject["auxiliaryType"];
  version: number;
}

export interface FinanceAuxiliaryOption {
  id: string;
  code: string;
  name: string;
}

export interface FinanceAuxiliaryOptions {
  customers: FinanceAuxiliaryOption[];
  suppliers: FinanceAuxiliaryOption[];
  departments: FinanceAuxiliaryOption[];
}

export interface VoucherEntry {
  id: string;
  lineNo: number;
  subjectId: string;
  subjectCode: string;
  subjectName: string;
  summary: string;
  debitAmount: number;
  creditAmount: number;
  auxiliaryType: "NONE" | "CUSTOMER" | "SUPPLIER" | "DEPARTMENT";
  auxiliaryId?: string;
  auxiliaryName?: string;
}

export interface Voucher {
  id: string;
  number: string;
  voucherType: "AUTO" | "MANUAL" | "OPENING" | "REVERSAL";
  sourceType?: string;
  sourceId?: string;
  sourceNo?: string;
  businessDate: string;
  summary: string;
  debitTotal: number;
  creditTotal: number;
  status: "DRAFT" | "AUDITED" | "POSTED" | "REVERSED" | "VOIDED";
  reversalVoucherId?: string;
  cashFlowCategory?: "OPERATING" | "INVESTING" | "FINANCING" | "UNCLASSIFIED";
  version: number;
  createdAt: string;
  entries: VoucherEntry[];
}

export interface VoucherCreateRequest {
  businessDate: string;
  summary: string;
  entries: Array<{
    subjectId: string;
    summary: string;
    debitAmount: number;
    creditAmount: number;
    auxiliaryType: "NONE" | "CUSTOMER" | "SUPPLIER" | "DEPARTMENT";
    auxiliaryId?: string | undefined;
    auxiliaryName?: string | undefined;
  }>;
}

export interface TrialBalanceRow {
  subjectId: string;
  subjectCode: string;
  subjectName: string;
  category: AccountSubject["category"];
  balanceDirection: "DEBIT" | "CREDIT";
  openingDebit: number;
  openingCredit: number;
  periodDebit: number;
  periodCredit: number;
  endingDebit: number;
  endingCredit: number;
}

export interface GeneralLedgerRow {
  entryId: string;
  voucherId?: string;
  businessDate: string;
  voucherNo: string;
  summary: string;
  debitAmount: number;
  creditAmount: number;
  runningBalance: number;
  balanceDirection: "DEBIT" | "CREDIT";
}

export * from "./finance-controls";
export * from "./reconciliations";
export * from "./voucher-attachments";
export * from "./open-api";
export * from "./customization";

export const PERMISSIONS = {
  analyticsRead: "ANALYTICS_READ",
  alertsManage: "ALERTS_MANAGE",
  workflowRead: "WORKFLOW_READ",
  workflowManage: "WORKFLOW_MANAGE",
  workflowApprove: "WORKFLOW_APPROVE",
  equipmentRead: "EQUIPMENT_READ",
  equipmentManage: "EQUIPMENT_MANAGE",
  equipmentAccept: "EQUIPMENT_ACCEPT",
  qualityRead: "QUALITY_READ",
  qualityManage: "QUALITY_MANAGE",
  qualityRelease: "QUALITY_RELEASE",
  qualityConcession: "QUALITY_CONCESSION",
  manufacturingRead: "MANUFACTURING_READ",
  manufacturingManage: "MANUFACTURING_MANAGE",
  manufacturingRelease: "MANUFACTURING_RELEASE",
  productionException: "PRODUCTION_EXCEPTION",
  planningRead: "PLANNING_READ",
  planningManage: "PLANNING_MANAGE",
  dashboardView: "DASHBOARD_VIEW",
  organizationRead: "ORG_USER_READ",
  organizationManage: "ORG_USER_MANAGE",
  platformTenantRead: "PLATFORM_TENANT_READ",
  platformTenantCreate: "PLATFORM_TENANT_CREATE",
  taskCenterRead: "TASK_CENTER_READ",
  taskCenterManage: "TASK_CENTER_MANAGE",
  masterDataRead: "MASTER_DATA_READ",
  masterDataManage: "MASTER_DATA_MANAGE",
  salesRead: "SALES_READ",
  salesManage: "SALES_MANAGE",
  salesAudit: "SALES_AUDIT",
  purchaseRead: "PURCHASE_READ",
  purchaseManage: "PURCHASE_MANAGE",
  purchaseAudit: "PURCHASE_AUDIT",
  inventoryRead: "INVENTORY_READ",
  inventoryManage: "INVENTORY_MANAGE",
  inventoryAudit: "INVENTORY_AUDIT",
  costPriceRead: "COST_PRICE_READ",
  settlementRead: "SETTLEMENT_READ",
  settlementManage: "SETTLEMENT_MANAGE",
  settlementAudit: "SETTLEMENT_AUDIT",
  financeRead: "FINANCE_READ",
  financeManage: "FINANCE_MANAGE",
  financePost: "FINANCE_POST",
  financeSensitiveRead: "FINANCE_SENSITIVE_READ",
  openApiManage: "OPEN_API_MANAGE",
  printTemplateManage: "PRINT_TEMPLATE_MANAGE",
  dataExchangeManage: "DATA_EXCHANGE_MANAGE",
} as const;

export * from "./manufacturing";
export * from "./planning";

export const hasPermission = (
  permissions: readonly string[],
  permission: string,
): boolean => permissions.includes(permission);

export const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat("zh-CN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

export const formatDate = (value: string): string =>
  new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));

export * from "./quality";
export * from "./equipment";

export * from "./workflow";

export * from "./analytics";
export * from "./trade-policies";
export * from "./purchase-expenses";
export * from "./background-tasks";
export * from "./inventory-counts";
export interface PlatformHostHealth {
  checkedAt: string | null;
  severity: "OK" | "WARN" | "CRITICAL" | "UNKNOWN";
  stale: boolean;
  checks: { code: string; status: string; message: string }[];
  diskPercent: number;
  availableMemoryMiB: number;
  loadPerCore: number;
  lastBackupAt: string | null;
  lastOffsiteAt: string | null;
  lastRestoreAt: string | null;
}

export type { CodeSuggestionKind } from "./nameCode";
export type { InventoryProductionDocument, InventoryProductionDocumentDetail } from "./production-stock";
